package br.gov.go.pge.permanenciaweb.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import br.gov.go.pge.permanenciaweb.shared.PublicConfigDto
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import br.gov.go.pge.permanenciaweb.site.components.PrimaryButtonVariant
import com.varabyte.kobweb.browser.http.bodyAsBytes
import com.varabyte.kobweb.browser.http.http
import com.varabyte.kobweb.compose.css.autoLength
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.flexDirection
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.serialization.json.Json
import org.jetbrains.compose.web.attributes.AutoComplete
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.FormMethod
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.autoComplete
import org.jetbrains.compose.web.attributes.method
import org.jetbrains.compose.web.attributes.name
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.required
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.url.URLSearchParams

val LoginCardStyle = CssStyle.base {
    Modifier
        .maxWidth(20.cssRem)
        .margin(top = 15.vh, leftRight = autoLength, bottom = 15.vh)
        .backgroundColor(IndustrialPalette.White)
        // 1px hairline borders stay in px per the units policy.
        .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
        .borderRadius(0.375.cssRem)
        .padding(1.5.cssRem)
        .display(DisplayStyle.Flex)
        .flexDirection(FlexDirection.Column)
        .gap(0.75.cssRem)
}

val LoginHintStyle = CssStyle.base {
    Modifier.color(IndustrialPalette.Muted).margin(0.cssRem)
}

/** The plain form is a block by default: without a column gap the inputs touch each other. */
val LoginFormStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .flexDirection(FlexDirection.Column)
        .gap(0.75.cssRem)
}

val LoginInputStyle = CssStyle.base {
    // Escape hatch: the native `<input>` keeps `name`/`autocomplete` for the plain form POST
    // (Silk's `Input` exposes no `name`), so it is styled with a `CssStyle` instead.
    Modifier
        .width(100.percent)
        .color(IndustrialPalette.Ink)
        .backgroundColor(IndustrialPalette.White)
        .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
        .borderRadius(0.25.cssRem)
        .padding(0.375.cssRem, 0.625.cssRem)
}

sealed interface LoginMessageBoxKind : ComponentKind

/** Box under the form telling the user why the login did not go through. */
val LoginMessageStyle = CssStyle<LoginMessageBoxKind> {
    base {
        Modifier
            .fontSize(0.75.cssRem)
            .padding(0.5.cssRem, 0.625.cssRem)
            .borderRadius(0.25.cssRem)
    }
}

val LoginErrorBoxVariant = LoginMessageStyle.addVariantBase {
    Modifier
        .backgroundColor(IndustrialPalette.RedTint)
        .color(IndustrialPalette.Red)
        .border(1.px, LineStyle.Solid, IndustrialPalette.RedTint)
}

val LoginInfoBoxVariant = LoginMessageStyle.addVariantBase {
    Modifier
        .backgroundColor(IndustrialPalette.SteelTint)
        .color(IndustrialPalette.Steel)
        .border(1.px, LineStyle.Solid, IndustrialPalette.SteelTint)
}

internal enum class LoginMessageKind { ERROR, INFO }

internal data class LoginMessage(val text: String, val kind: LoginMessageKind)

/**
 * Message for the `?error=` code the server sends back. Kept pure so the jsTest suite can pin
 * every wording without a browser.
 */
internal fun loginMessage(code: String?, contactEmail: String): LoginMessage? = when (code) {
    "credenciais" -> LoginMessage("Usuário ou senha inválidos.", LoginMessageKind.ERROR)
    "sessao" -> LoginMessage("Sua sessão expirou. Entre novamente.", LoginMessageKind.ERROR)
    "aguardando" -> LoginMessage(
        "Seu usuário ainda não foi ativado; uma solicitação de acesso foi enviada ao administrador. " +
            "Para mais informações, ${contactWording(contactEmail)}.",
        LoginMessageKind.INFO,
    )
    "negado" -> LoginMessage(
        "Seu acesso não foi permitido pelo administrador. " +
            "Se você acredita que houve um engano, ${contactWording(contactEmail)}.",
        LoginMessageKind.ERROR,
    )
    "indisponivel" -> LoginMessage(
        "Não foi possível validar suas credenciais agora (serviço de autenticação indisponível). " +
            "Tente novamente em instantes.",
        LoginMessageKind.ERROR,
    )
    else -> null
}

private fun contactWording(contactEmail: String): String =
    if (contactEmail.isBlank()) "contate o administrador" else "contate $contactEmail"

private val json = Json { ignoreUnknownKeys = true }

/**
 * Login page, ported from the old static `login.html`: a native form POST to `/login`
 * (browser default `application/x-www-form-urlencoded`), a message box per `?error=...` code
 * returned by the server, and a redirect to `/` handled by the server's 302.
 */
@Page
@Composable
fun Login() {
    var contactEmail by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        document.title = "Entrar — Permanência no Prédio"
        // Public config only carries the contact address shown in the message boxes.
        contactEmail = runCatching {
            val response = window.http.get("/api/public-config")
            if (response.ok) {
                json.decodeFromString<PublicConfigDto>(response.bodyAsBytes().decodeToString()).contactEmail
            } else {
                ""
            }
        }.getOrDefault("")
    }

    val message = loginMessage(URLSearchParams(window.location.search).get("error"), contactEmail)

    Main(attrs = LoginCardStyle.toAttrs()) {
        H1(attrs = Modifier.fontSize(1.125.cssRem).margin(0.cssRem).toAttrs()) { Text("Permanência no Prédio") }
        P(attrs = LoginHintStyle.toAttrs()) { Text("Acesso restrito à equipe de segurança.") }
        Form(action = "/login", attrs = LoginFormStyle.toAttrs { method(FormMethod.Post) }) {
            Input(type = InputType.Text, attrs = LoginInputStyle.toAttrs {
                name("username")
                placeholder("Usuário")
                autoComplete(AutoComplete.username)
                required()
            })
            Input(type = InputType.Password, attrs = LoginInputStyle.toAttrs {
                name("password")
                placeholder("Senha")
                autoComplete(AutoComplete.currentPassword)
                required()
            })
            if (message != null) {
                val variant = if (message.kind == LoginMessageKind.INFO) LoginInfoBoxVariant else LoginErrorBoxVariant
                Div(
                    attrs = LoginMessageStyle.toModifier(variant)
                        .then(Modifier.id("login-message"))
                        .toAttrs()
                ) {
                    Text(message.text)
                }
            }
            Button(
                onClick = {},
                type = ButtonType.Submit,
                variant = PrimaryButtonVariant,
                size = ButtonSize.SM,
                modifier = Modifier.width(100.percent),
            ) {
                Text("Entrar")
            }
        }
    }
}
